import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Box, Stack, Typography } from "@mui/material";
import { Colors } from "Components/Layout/Themes/Colors";
import { BlTextButton } from "Components/Shared/Buttons/BlTextButton";
import { CloseIcon } from "Components/Shared/Icons";
import { EditIcon } from "Components/Shared/Icons/Edit";
import { Resources, useResource } from "Translations/Resources";
import type { ReactNode } from "react";

const RESOURCES = Resources.Components.Shared.CardsList;

export type CardListItemType = {
  id: string | number;
  title: ReactNode;
  subtitle: ReactNode;
  onDelete?: () => void;
  onEdit?: () => void;
  onView?: () => void;
  onDownload?: () => void;
  isDisabled?: boolean;
};

type Props = { items: Array<CardListItemType> };

export const CardsList = ({ items }: Props) => {
  const { t } = useResource();

  return (
    <Box component="ul" p={0} m={0}>
      {items.map(
        ({
          id,
          title,
          subtitle,
          isDisabled,
          onDownload,
          onView,
          onEdit,
          onDelete,
        }) => (
          <CardListItem key={id}>
            <Stack justifyContent="space-between">
              {title}
              {subtitle}
            </Stack>
            <Box display="flex" gap={3} alignItems="center">
              {onDownload && (
                <BlTextButton onClick={onDownload} disabled={isDisabled}>
                  <Typography
                    component="span"
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                    fontWeight={600}
                  >
                    <FileDownloadOutlinedIcon
                      aria-label={t(RESOURCES.Download)}
                      sx={{ color: isDisabled ? Colors.DarkGrey : undefined }}
                    />
                    {t(RESOURCES.Download)}
                  </Typography>
                </BlTextButton>
              )}
              {onView && (
                <BlTextButton onClick={onView} disabled={isDisabled}>
                  <VisibilityOutlinedIcon aria-label={t(RESOURCES.View)} />
                </BlTextButton>
              )}
              {onEdit && (
                <BlTextButton onClick={onEdit} disabled={isDisabled}>
                  <EditIcon
                    width="20px"
                    height="20px"
                    aria-label={t(RESOURCES.Edit)}
                    color={isDisabled ? Colors.DarkGrey : undefined}
                  />
                </BlTextButton>
              )}
              {onDelete && (
                <BlTextButton onClick={onDelete} disabled={isDisabled}>
                  <CloseIcon
                    width="20px"
                    height="20px"
                    aria-label={t(RESOURCES.Delete)}
                    color={isDisabled ? Colors.DarkGrey : Colors.DeleteRed}
                  />
                </BlTextButton>
              )}
            </Box>
          </CardListItem>
        ),
      )}
    </Box>
  );
};

type ListItemProps = {
  children: ReactNode;
};

const CardListItem = ({ children }: ListItemProps) => (
  <Box
    component="li"
    sx={({ colors }) => ({
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      flexWrap: "wrap-reverse",
      border: `1px solid ${colors.border}`,
      borderRadius: 2,
      my: 2,
      py: 2,
      px: 3,
    })}
  >
    {children}
  </Box>
);
